import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Button,
  IconButton,
  Chip,
  CircularProgress,
  Menu,
  MenuItem,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  Select,
  OutlinedInput,
  Checkbox,
  ListItemText,
  InputBase,
} from "@mui/material";
import { COLORS, FONT_FAMILY, TYPOGRAPHY } from "../../constants/color";
import { Icon } from "../../icons";
import CustomTooltip from "../../components/CustomTooltip";
import { useAuth } from "../../context/authContext";
import axios from "axios";
import { url } from "../../constants/url";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import ClientDetailsSidebar from "../../components/siteCheck/ViewChecklistsDetails";
import EditChecklistSidebarWithToast from "../../components/siteCheck/EditCheckList";
import { useNavigate } from "react-router-dom";
import ClientNotificationBanner from "../../components/ClientNotificationBanner";
import ConfirmDialog from "../../components/ui/ConfirmDialog";
import { toast } from "react-hot-toast";

// Status styles mapping
const statusStyles = {
  UNASSIGNED: { bgcolor: "#F8D66D", color: "#fff" },
  ASSIGNED: { bgcolor: "#FFB54C", color: "#fff" },
  VISITED: { bgcolor: "#B3EBF2", color: "#fff" },
  SIGNPENDING: { bgcolor: "#A5E3C2", color: "#fff" },
  FINALREPORT: { bgcolor: "#8CD47E", color: "#fff" },
  REJECTED: { bgcolor: "#FF6961", color: "#fff" },
};

// Status display names
const statusDisplayNames = {
  UNASSIGNED: "Unassigned",
  ASSIGNED: "Assigned",
  VISITED: "Visited",
  SIGNPENDING: "Sign Pending",
  FINALREPORT: "Final Report",
  REJECTED: "Rejected",
};

const StatusBasedProjects = ({ status, title }) => {
  const { userData, isAuthenticated, hasRole, token, refreshUserData } = useAuth();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalProjects, setTotalProjects] = useState(0);
  const [pageSize] = useState(12);

  const [selectedProject, setSelectedProject] = useState(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isEditScreenOpen, setIsEditScreenOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, id: null, status: null });

  // Filter and sort states
  const [selectedSort, setSelectedSort] = useState("latest");
  const [sortConfig, setSortConfig] = useState({
    column: null,
    direction: null,
  });

  const [downloadingProjectId, setDownloadingProjectId] = useState(null);

  // Menu anchors
  const [sortAnchorEl, setSortAnchorEl] = useState(null);
  const [calendarAnchorEl, setCalendarAnchorEl] = useState(null);

  const navigate = useNavigate();
  const [adminDetails, setAdminDetails] = useState(null);
  const [adminLoading, setAdminLoading] = useState(false);
  // Date filter states
  const [dateFilter, setDateFilter] = useState("ALL");
  const [customDateRange, setCustomDateRange] = useState({
    from: null,
    to: null,
  });
  const [showDatePicker, setShowDatePicker] = useState(false);

  // Filter states
  const [engineers, setEngineers] = useState([]);
  const [clients, setClients] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [selectedEngineers, setSelectedEngineers] = useState([]);
  const [selectedClients, setSelectedClients] = useState([]);
  const [selectedTemplates, setSelectedTemplates] = useState([]);
  const [loadingFilters, setLoadingFilters] = useState({
    engineers: false,
    clients: false,
    templates: false,
  });

  // Show filter section state
  const [showFilters, setShowFilters] = useState(false);

  // Search state
  const [searchQuery, setSearchQuery] = useState("");
  const [searchDebounce, setSearchDebounce] = useState("");

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchDebounce(searchQuery);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Fetch projects based on the specific status
  const fetchProjects = useCallback(async () => {
    if (!isAuthenticated || !token || !(hasRole("CLIENT") || hasRole("ADMIN") || hasRole("SUPERADMIN") || hasRole("ENGINEER"))) {
      setLoading(false);
      return;
    }

    // Hide UNASSIGNED screen for ENGINEER role
    if (hasRole("ENGINEER") && status === "UNASSIGNED") {
      setProjects([]);
      setTotalProjects(0);
      setTotalPages(1);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Determine sort parameter
      let sortParam = selectedSort;

      if (sortConfig.column && sortConfig.direction) {
        if (sortConfig.column === 'projectName') {
          sortParam = sortConfig.direction === 'asc' ? 'projectAz' : 'projectZa';
        } else if (sortConfig.column === 'clientName') {
          sortParam = sortConfig.direction === 'asc' ? 'clientAz' : 'clientZa';
        } else if (sortConfig.column === 'visitDate') {
          sortParam = sortConfig.direction === 'asc' ? 'visitDateAsc' : 'visitDateDesc';
        }
      }

      // Prepare date filter for visit date
      let visitDateFrom = null;
      let visitDateTo = null;

      if (dateFilter === "TODAY") {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        visitDateFrom = today.toISOString();

        const todayEnd = new Date(today);
        todayEnd.setHours(23, 59, 59, 999);
        visitDateTo = todayEnd.toISOString();
      } else if (dateFilter === "YESTERDAY") {
        const yesterday = new Date();
        yesterday.setDate(yesterday.getDate() - 1);
        yesterday.setHours(0, 0, 0, 0);
        visitDateFrom = yesterday.toISOString();

        const yesterdayEnd = new Date(yesterday);
        yesterdayEnd.setHours(23, 59, 59, 999);
        visitDateTo = yesterdayEnd.toISOString();
      } else if (dateFilter === "TOMORROW") {
        const tomorrow = new Date();
        tomorrow.setDate(tomorrow.getDate() + 1);
        tomorrow.setHours(0, 0, 0, 0);
        visitDateFrom = tomorrow.toISOString();

        const tomorrowEnd = new Date(tomorrow);
        tomorrowEnd.setHours(23, 59, 59, 999);
        visitDateTo = tomorrowEnd.toISOString();
      } else if (dateFilter === "CUSTOM" && customDateRange.from && customDateRange.to) {
        const from = new Date(customDateRange.from);
        from.setHours(0, 0, 0, 0);
        visitDateFrom = from.toISOString();

        const to = new Date(customDateRange.to);
        to.setHours(23, 59, 59, 999);
        visitDateTo = to.toISOString();
      }

      // Prepare params based on role
      const params = {
        page: page,
        limit: pageSize,
        sort: sortParam,
        status: status, // Always filter by the specific status passed as prop
      };

      // Add search query if exists
      if (searchDebounce.trim()) {
        params.search = searchDebounce.trim();
      }

      // Add role-based filters
      if (hasRole("CLIENT") && userData?._id) {
        params.clientID = userData._id;
      }

      // Add multi-select filters
      if (selectedEngineers.length > 0) {
        params.engineerName = selectedEngineers.join(",");
      }

      if (selectedClients.length > 0) {
        params.clientName = selectedClients.join(",");
      }

      if (selectedTemplates.length > 0) {
        params.siteCheckType = selectedTemplates.join(",");
      }

      // Add date filters for visit date
      if (visitDateFrom && visitDateTo) {
        params.visitDateFrom = visitDateFrom;
        params.visitDateTo = visitDateTo;
      }

      // Make API call based on role
      let response;
      if (hasRole("ENGINEER") && userData?._id) {
        // For engineers, use assigned projects endpoint
        // ✅ IMPORTANT: Engineer API uses dateFrom/dateTo, not visitDateFrom/visitDateTo
        if (visitDateFrom) params.dateFrom = visitDateFrom;
        if (visitDateTo) params.dateTo = visitDateTo;

        response = await axios.get(`${url}/api/engineer/assigned-projects/${userData._id}`, {
          headers: { Authorization: `Bearer ${token}` },
          params: params
        });

        const assignedProjects = response.data?.assignedProjects || [];
        const total = response.data?.total || 0;

        setProjects(transformProjects(assignedProjects));
        setTotalProjects(total);
        setTotalPages(Math.ceil(total / pageSize));

      } else {
        // For other roles, use regular requests endpoint
        response = await axios.get(`${url}/api/client/requests`, {
          headers: { Authorization: `Bearer ${token}` },
          params: params
        });

        const allRequests = response.data?.requests || [];
        const total = response.data?.total || 0;

        setProjects(transformProjects(allRequests));
        setTotalProjects(total);
        setTotalPages(Math.ceil(total / pageSize));
      }

    } catch (err) {
      console.error("Error fetching projects:", err);
      setError(err.response?.data?.message || "Failed to load projects");
      setProjects([]);
      setTotalProjects(0);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  }, [
    isAuthenticated, token, hasRole, userData, page, pageSize,
    selectedSort, sortConfig, dateFilter, customDateRange,
    selectedEngineers, selectedClients, selectedTemplates,
    searchDebounce, status
  ]);

  // Main useEffect for fetching data
  useEffect(() => {
    if (isAuthenticated && (hasRole("CLIENT") || hasRole("ADMIN") || hasRole("SUPERADMIN") || hasRole("ENGINEER"))) {
      fetchProjects();
      if (showFilters) {
        fetchFilterData();
      }
    } else {
      setLoading(false);
    }
  }, [
    userData, token, page, selectedSort, sortConfig,
    dateFilter, customDateRange, selectedEngineers, selectedClients,
    selectedTemplates, showFilters, searchDebounce, status,
    fetchProjects
  ]);

  // Effect for handling page focus/visibility changes
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        fetchProjects();
      }
    };

    const handleFocus = () => {
      fetchProjects();
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("focus", handleFocus);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("focus", handleFocus);
    };
  }, [fetchProjects]);

  // Fetch filter data
  const fetchFilterData = async () => {
    try {
      // Fetch engineers (only for ADMIN/SUPERADMIN)
      if ((hasRole("ADMIN") || hasRole("SUPERADMIN")) && !engineers.length) {
        setLoadingFilters(prev => ({ ...prev, engineers: true }));
        const engineersRes = await axios.get(`${url}/api/users/fetch`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { role: "ENGINEER", status: "APPROVED", sort: "az" }
        });
        setEngineers(engineersRes.data?.users || []);
        setLoadingFilters(prev => ({ ...prev, engineers: false }));
      }

      // Fetch clients (for ADMIN/SUPERADMIN and ENGINEER)
      if ((hasRole("ADMIN") || hasRole("SUPERADMIN") || hasRole("ENGINEER")) && !clients.length) {
        setLoadingFilters(prev => ({ ...prev, clients: true }));
        const clientsRes = await axios.get(`${url}/api/users/fetch`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { role: "CLIENT", status: "APPROVED", sort: "az" }
        });
        setClients(clientsRes.data?.users || []);
        setLoadingFilters(prev => ({ ...prev, clients: false }));
      }

      // Fetch templates (for all roles)
      if (!templates.length) {
        setLoadingFilters(prev => ({ ...prev, templates: true }));
        const templatesRes = await axios.get(`${url}/api/admin/checklist-templates`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { isActive: true, limit: 100 }
        });
        setTemplates(templatesRes.data?.data || []);
        setLoadingFilters(prev => ({ ...prev, templates: false }));
      }

    } catch (err) {
      console.error("Error fetching filter data:", err);
    }
  };

  // Helper function to transform project data
  const transformProjects = (requests) => {
    return requests.map(item => {
      // Format Request Date
      let reqDateDisplay = "N/A";

      if (item.preferredSiteVisitDate?.fromDate) {
        const fromDate = new Date(item.preferredSiteVisitDate.fromDate);
        const toDate = item.preferredSiteVisitDate.toDate
          ? new Date(item.preferredSiteVisitDate.toDate)
          : null;

        if (!isNaN(fromDate)) {
          const fromFormatted = formatDate(fromDate);

          if (toDate && !isNaN(toDate)) {
            const toFormatted = formatDate(toDate);
            reqDateDisplay =
              fromDate.toDateString() === toDate.toDateString()
                ? fromFormatted
                : `${fromFormatted} - ${toFormatted}`;
          } else {
            reqDateDisplay = fromFormatted;
          }
        }
      }
      else if (item.requestedDate) {
        const requested = new Date(item.requestedDate);
        if (!isNaN(requested)) {
          reqDateDisplay = formatDate(requested);
        }
      }

      // Format VISIT date - FIXED: Use assignedVisitDate to match AllProjects.js
      let visitedDateDisplay = "Not Scheduled";
      if (item.assignedVisitDate) {
        try {
          let date = null;
          if (item.assignedVisitDate.$date) {
            date = new Date(item.assignedVisitDate.$date);
          } else if (item.assignedVisitDate instanceof Date || typeof item.assignedVisitDate === 'string') {
            date = new Date(item.assignedVisitDate);
          }

          if (date && !isNaN(date.getTime())) {
            visitedDateDisplay = formatDate(date);
          }
        } catch (error) {
          console.error("Error formatting visited date:", error);
        }
      }

      // Get client name from different sources based on role
      let clientName = "N/A";
      let clientContact = "N/A";
      let clientAddress = "N/A";

      if (hasRole("ENGINEER")) {
        // Engineer API returns populated clientID
        clientName = item.clientID?.name || item.clientID?.companyName || item.nameOfClient || "N/A";
        clientContact = item.clientID?.phone || item.phoneOfClient || "N/A";
        clientAddress = item.detailedAddress || "N/A";
      } else {
        // Regular API for other roles
        clientName = item.nameOfClient || "N/A";
        clientContact = item.phoneOfClient || "N/A";
        clientAddress = item.detailedAddress || "N/A";
      }

      // Get engineer details
      let engineerName = "Not Assigned";
      let engineerContact = "";

      if (item.assignedEngineer) {
        if (typeof item.assignedEngineer === 'object') {
          engineerName = item.assignedEngineer.name || "Not Assigned";
          engineerContact = item.assignedEngineer.phone || "";
        } else {
          engineerName = "Not Assigned";
        }
      }

      // Get project report number
      const projectReportNo = item.projectReportNo || "—";

      // Get status with role-based modifications
      let displayStatus = item.status || "UNKNOWN";

      // For CLIENT role, convert SIGNPENDING to VISITED
      if (hasRole("CLIENT") && displayStatus === "SIGNPENDING") {
        displayStatus = "VISITED";
      }

      return {
        id: item._id || item.id,
        projectReportNo: projectReportNo,
        projectName: item.nameOfProject || "N/A",
        clientName: clientName,
        clientContact: clientContact,
        clientAddress: clientAddress,
        siteCheck: item.templateTitle || (item.templateID?.title || "N/A"),
        reqDate: reqDateDisplay,
        engineer: engineerName,
        engineerContact: engineerContact,
        assignedVisitDate: visitedDateDisplay,
        status: displayStatus,
        originalStatus: item.status || "UNKNOWN",
        _id: item._id,
      };
    });
  };

  // Helper function to format date
  const formatDate = (date) => {
    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "2-digit",
    }).replace(/\//g, '/')
  }

  // Handle page change
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage);
    }
  };

  // Calculate displayed range
  const startIndex = totalProjects > 0 ? ((page - 1) * pageSize) + 1 : 0;
  const endIndex = Math.min(page * pageSize, totalProjects);

  // Check if any filter is applied
  const isFilterApplied = () => {
    return selectedSort !== "latest" ||
      sortConfig.column !== null ||
      dateFilter !== "ALL" ||
      selectedEngineers.length > 0 ||
      selectedClients.length > 0 ||
      selectedTemplates.length > 0 ||
      searchQuery.trim() !== "";
  };

  // Clear all filters
  const clearFilters = () => {
    setSelectedSort("latest");
    setSortConfig({ column: null, direction: null });
    setDateFilter("ALL");
    setCustomDateRange({ from: null, to: null });
    setSelectedEngineers([]);
    setSelectedClients([]);
    setSelectedTemplates([]);
    setSearchQuery("");
    setPage(1);
  };

  // Clear search only
  const clearSearch = () => {
    setSearchQuery("");
    setPage(1);
  };

  // Toggle filter section
  const toggleFilters = () => {
    setShowFilters(!showFilters);
    if (!showFilters) {
      fetchFilterData();
    }
  };

  // Handle column sort click
  const handleColumnSort = (column) => {
    setSortConfig(prev => {
      if (prev.column === column) {
        return {
          column: column,
          direction: prev.direction === 'asc' ? 'desc' : 'asc'
        };
      } else {
        return {
          column: column,
          direction: 'asc'
        };
      }
    });
  };

  // Get sort icon and color for a column
  const getSortIconForColumn = (column) => {
    if (sortConfig.column === column) {
      return sortConfig.direction === 'asc' ? 'foldUp' : 'foldDown';
    }
    return 'sort';
  };

  const getSortIconColorForColumn = (column) => {
    if (sortConfig.column === column) {
      return COLORS.PRIMARY;
    }
    return COLORS.LIGHT_SECONDARY;
  };

  // Handle view details
  const handleViewDetails = (project) => {
    setSelectedProject(project._id);
    setIsSidebarOpen(true);
  };

  // Handle edit
  const handleEdit = async (projectId) => {
    setSelectedProject(projectId);
    setIsEditMode(true);
    setIsEditScreenOpen(true);
  };

  const fetchAdminDetails = useCallback(async () => {
    if (!hasRole("CLIENT") || !token) return;

    try {
      setAdminLoading(true);
      // Fetch users with ADMIN role
      const response = await axios.get(`${url}/api/users/fetch`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          role: "ADMIN",
          status: "APPROVED",
          limit: 1 // Get first admin
        }
      });

      if (response.data?.users && response.data.users.length > 0) {
        // Use the first admin's contact details
        const admin = response.data.users[0];
        setAdminDetails({
          name: admin.name || "Admin",
          phone: admin.phone || "+91 9999999999", // Default fallback
          email: admin.email || "admin@example.com"
        });
      } else {
        // Set default admin details if no admin found
        setAdminDetails({
          name: "Admin",
          phone: "+91 9999999999",
          email: "admin@example.com"
        });
      }
    } catch (err) {
      console.error("Error fetching admin details:", err);
      // Set default admin details on error
      setAdminDetails({
        name: "Admin",
        phone: "+91 9999999999",
        email: "admin@example.com"
      });
    } finally {
      setAdminLoading(false);
    }
  }, [hasRole, token]);
  // Handle delete - Only for ADMIN/SUPERADMIN
  const handleDelete = (project) => {
    setDeleteConfirm({ open: true, id: project.id, status: project.originalStatus || project.status });
  };

  const executeDelete = async () => {
    if (!isAuthenticated || !deleteConfirm.id) return;

    try {
      if (hasRole("ADMIN") || hasRole("SUPERADMIN")) {
        await axios.delete(`${url}/api/client/requests/${deleteConfirm.id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        toast.success("Request deleted successfully");
        refreshUserData();
        fetchProjects();
      }
    } catch (err) {
      console.error("Delete error:", err);
      toast.error(err.response?.data?.message || "Failed to delete request");
    } finally {
      setDeleteConfirm({ open: false, id: null, status: null });
    }
  };

  // Handle download report
  const handleDownloadReport = async (projectId) => {
    try {
      if (!token) {
        toast.error("Please login to download reports");
        return;
      }

      setDownloadingProjectId(projectId);

      const userRole = userData?.role || "CLIENT";

      const response = await axios.get(
        `${url}/api/admin/download-checklist-report/${projectId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          params: {
            userRole: userRole
          },
          responseType: 'blob',
        }
      );

      const pdfBlob = new Blob([response.data], { type: 'application/pdf' });
      const downloadUrl = window.URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');
      link.href = downloadUrl;

      const contentDisposition = response.headers['content-disposition'];
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="([^"]+)"/i);
        if (match && match[1]) {
          link.download = match[1];
        } else {
          link.download = `checklist-report-${projectId}.pdf`;
        }
      } else {
        link.download = `checklist-report-${projectId}.pdf`;
      }

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);

    } catch (err) {
      console.error("Error downloading report:", err);

      let errorMsg = "Failed to download report";
      if (err.response?.status === 404) {
        errorMsg = "Report not found for this project";
      } else if (err.response?.status === 400) {
        errorMsg = "Report is not ready for download";
      } else if (err.response?.data?.message) {
        errorMsg = err.response.data.message;
      }

      toast.error(errorMsg);
    } finally {
      setDownloadingProjectId(null);
    }
  };

  // Handle add new site check
  const handleAddNewSiteCheck = () => {
    navigate("/projects/add-site-check");
  };

  // Refresh projects list
  const handleRefresh = () => {
    fetchProjects();
  };

  // Handle calendar filter selection
  const handleCalendarFilterSelect = (filterValue) => {
    if (filterValue === "CUSTOM") {
      setShowDatePicker(true);
      setCalendarAnchorEl(null);
    } else {
      setDateFilter(filterValue);
      setCalendarAnchorEl(null);
    }
  };

  // Handle custom date range selection
  const handleCustomDateRangeSelect = () => {
    if (customDateRange.from && customDateRange.to) {
      setDateFilter("CUSTOM");
      setShowDatePicker(false);
    }
  };

  // Define table headers with role-based columns
  const getTableHeaders = () => {
    const headers = [
      { key: "Project Name", hasSort: true, sortKey: "projectName", width: "160px" },
      { key: "Client Name", hasSort: true, sortKey: "clientName", width: "130px" },
    ];

    // Add Engineer column for ADMIN and SUPERADMIN only
    if (hasRole("ADMIN") || hasRole("SUPERADMIN")) {
      headers.push({ key: "Engineer", hasSort: false, width: "120px" });
    }

    // Add remaining columns
    headers.push(
      { key: "Site Check", hasSort: false, width: "100px" },
      { key: "Req Date", hasSort: false, width: "80px" },
      { key: "Visit Date", hasSort: true, sortKey: "visitDate", width: "100px" },
      { key: "Status", hasSort: false, width: "90px" },
      { key: "Actions", hasSort: false, width: "100px" }
    );

    return headers;
  };

  // Get date filter display text
  const getDateFilterDisplay = () => {
    if (dateFilter === "ALL") return "All Dates";
    if (dateFilter === "TODAY") return "Today";
    if (dateFilter === "YESTERDAY") return "Yesterday";
    if (dateFilter === "TOMORROW") return "Tomorrow";
    if (dateFilter === "CUSTOM" && customDateRange.from && customDateRange.to) {
      const from = formatDate(new Date(customDateRange.from));
      const to = formatDate(new Date(customDateRange.to));
      return `${from} - ${to}`;
    }
    return "Select Date";
  };

  // Show message if user doesn't have access
  if (!isAuthenticated || !(hasRole("CLIENT") || hasRole("ADMIN") || hasRole("SUPERADMIN") || hasRole("ENGINEER"))) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="h6" color="error">
          This page is only accessible to CLIENT, ADMIN, SUPERADMIN, or ENGINEER users.
        </Typography>
      </Box>
    );
  }

  // Hide UNASSIGNED screen for ENGINEER role
  if (hasRole("ENGINEER") && status === "UNASSIGNED") {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="h6" color="error">
          You don't have access to unassigned projects.
        </Typography>
      </Box>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box>
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 3,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  borderRadius: '50%',
                  // backgroundColor: statusStyles[status]?.bgcolor || COLORS.PRIMARY,
                }}
              />
              <Typography
                sx={{ ...TYPOGRAPHY.H2, fontFamily: FONT_FAMILY, color: COLORS.PRIMARY }}
              >
                {title || `${statusDisplayNames[status]} Projects`}
              </Typography>
            </Box>

            {hasRole("CLIENT") && (
              <Button
                startIcon={<Icon name="plus2" />}
                sx={{
                  bgcolor: COLORS.SECONDARY,
                  color: "#fff",
                  textTransform: "none",
                  borderRadius: 2,
                  ...TYPOGRAPHY.H4,
                  px: 2.5,
                  "&:hover": { bgcolor: COLORS.SECONDARY },
                }}
                onClick={handleAddNewSiteCheck}
              >
                Add New Site Check
              </Button>
            )}
          </Box>

          <Box sx={{ display: "flex", gap: 1, alignItems: 'center' }}>
            {/* Search Bar */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                flexGrow: 0,
                width: { xs: 180, sm: 250, md: 280 },
                px: 2,
                py: 0.7,
                borderRadius: 1.5,
                border: `1px solid ${COLORS.PRIMARY}`,
                backgroundColor: COLORS.BACKGROUND,
                color: COLORS.PRIMARY,
              }}
            >
              <Icon name="search" sx={{ fontSize: 18, color: "currentColor" }} />
              <InputBase
                placeholder="Search"
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setPage(1);
                }}
                sx={{
                  flex: 1,
                  fontFamily: FONT_FAMILY,
                  ...TYPOGRAPHY.BODY_STANDARD,
                  color: COLORS.TEXT,
                  ml: 1,
                  mt: 0.5,
                  '& input::placeholder': {
                    fontSize: '18px',   // increase size here
                    // optional
                  },
                }}
              />
              {searchQuery && (
                <IconButton
                  size="small"
                  onClick={clearSearch}
                  sx={{ padding: 0.5 }}
                >
                  <Icon name="close2" sx={{ fontSize: 14 }} color={COLORS.ERROR} />
                </IconButton>
              )}
            </Box>

            {/* Calendar Menu */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <CustomTooltip title="Calendar Filter">
                <IconButton
                  onClick={(e) => setCalendarAnchorEl(e.currentTarget)}
                  sx={{
                    color: dateFilter !== "ALL" ? COLORS.PRIMARY : COLORS.SECONDARY,
                  }}
                >
                  <Icon name="calendar2" />
                </IconButton>
              </CustomTooltip>
            </Box>

            {/* Sort Menu */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <CustomTooltip title="Sort Options">
                <IconButton
                  onClick={(e) => setSortAnchorEl(e.currentTarget)}
                  sx={{
                    color: selectedSort !== "latest" ? COLORS.PRIMARY : COLORS.SECONDARY,
                  }}
                >
                  <Icon name="sort" />
                </IconButton>
              </CustomTooltip>
            </Box>

            {/* Filter Button */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <CustomTooltip title="Show Filters">
                <IconButton
                  onClick={toggleFilters}
                  sx={{
                    color: showFilters ? COLORS.PRIMARY : COLORS.SECONDARY,
                  }}
                >
                  <Icon name="list" />
                </IconButton>
              </CustomTooltip>
            </Box>

            {isFilterApplied() && (
              <CustomTooltip title="Clear Filters">
                <IconButton onClick={clearFilters}>
                  <Icon name="rotate" color={COLORS.ERROR} />
                </IconButton>
              </CustomTooltip>
            )}
          </Box>
        </Box>

        {/* Client Note */}
        {/* {hasRole("CLIENT") && (
          <ClientNotificationBanner
            prependContent={
              <>
                <strong>Note:</strong> Preferred time should be between <strong>9:00 AM to 7:00 PM</strong>. For rescheduling or cancelling, call {adminLoading ? "Loading..." : (adminDetails?.phone || "+91 9999999999")}
              </>
            }
          />
        )} */}

        {/* Filter Section */}
        {/* Filter Section */}
        {showFilters && (
          <Box sx={{
            display: 'flex',
            justifyContent: 'space-between',
            gap: 2,
            mb: 3,
            alignItems: 'flex-start',
            flexWrap: 'wrap'
          }}>
            {/* Engineer Filter (only for ADMIN/SUPERADMIN) */}
            {(hasRole("ADMIN") || hasRole("SUPERADMIN")) && (
              <Box sx={{ flex: 1, minWidth: 180 }}>
                <Typography
                  sx={{
                    ...TYPOGRAPHY.BODY_SMALL,
                    color: COLORS.PRIMARY,
                    mb: 0.5,
                    fontWeight: 600
                  }}
                >
                  Engineer
                </Typography>
                <FormControl fullWidth size="small">
                  <Select
                    multiple
                    value={selectedEngineers}
                    onChange={(e) => {
                      setSelectedEngineers(e.target.value);
                      setPage(1);
                    }}
                    input={<OutlinedInput />}
                    renderValue={(selected) => {
                      if (selected.length === 0) {
                        return (
                          <Typography sx={{
                            color: COLORS.LIGHT_SECONDARY,
                            fontSize: '0.875rem'
                          }}>
                            Select Engineers
                          </Typography>
                        );
                      }
                      return selected.length <= 2
                        ? selected.join(', ')
                        : `${selected.length} selected`;
                    }}
                    displayEmpty
                    sx={{
                      backgroundColor: '#fff',
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: COLORS.PRIMARY,
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: COLORS.PRIMARY,
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: COLORS.PRIMARY,
                      },
                    }}
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          maxHeight: 200,
                          width: 280,
                          overflow: 'auto',
                          marginTop: '4px',
                          boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.15)',
                          border: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                          '& .MuiMenuItem-root': {
                            minHeight: 36,
                            padding: '4px 12px',
                            fontSize: '0.85rem',
                          },
                          '& .MuiCheckbox-root': {
                            padding: '4px 6px 4px 4px',
                          }
                        }
                      },
                    }}
                  >
                    {loadingFilters.engineers ? (
                      <MenuItem disabled sx={{ justifyContent: 'center', py: 1 }}>
                        <CircularProgress size={18} />
                      </MenuItem>
                    ) : engineers.length > 0 ? (
                      engineers.map((engineer) => (
                        <MenuItem
                          key={engineer._id}
                          value={engineer.name}
                          sx={{
                            whiteSpace: 'normal',
                            wordBreak: 'break-word',
                            '&:hover': {
                              backgroundColor: 'rgba(0, 0, 0, 0.04)',
                            }
                          }}
                        >
                          <Checkbox
                            checked={selectedEngineers.indexOf(engineer.name) > -1}
                            size="small"
                            sx={{ '& svg': { fontSize: '0.9rem' } }}
                          />
                          <ListItemText
                            primary={engineer.name}
                            sx={{
                              margin: 0,
                              '& .MuiTypography-root': {
                                fontSize: '0.85rem',
                                fontWeight: 400,
                                lineHeight: 1.2,
                              }
                            }}
                          />
                        </MenuItem>
                      ))
                    ) : (
                      <MenuItem disabled sx={{ py: 1 }}>
                        <Typography sx={{
                          fontSize: '0.85rem',
                          color: COLORS.TEXT,
                          fontStyle: 'italic'
                        }}>
                          No engineers available
                        </Typography>
                      </MenuItem>
                    )}
                  </Select>
                </FormControl>
              </Box>
            )}

            {/* Client Filter */}
            {(hasRole("ADMIN") || hasRole("SUPERADMIN") || hasRole("ENGINEER")) && (
              <Box sx={{ flex: 1, minWidth: 180 }}>
                <Typography
                  sx={{
                    ...TYPOGRAPHY.BODY_SMALL,
                    color: COLORS.PRIMARY,
                    mb: 0.5,
                    fontWeight: 600
                  }}
                >
                  Client
                </Typography>
                <FormControl fullWidth size="small">
                  <Select
                    multiple
                    value={selectedClients}
                    onChange={(e) => {
                      setSelectedClients(e.target.value);
                      setPage(1);
                    }}
                    input={<OutlinedInput />}
                    renderValue={(selected) => {
                      if (selected.length === 0) {
                        return (
                          <Typography sx={{
                            color: COLORS.LIGHT_SECONDARY,
                            fontSize: '0.875rem'
                          }}>
                            Select Clients
                          </Typography>
                        );
                      }
                      return selected.length <= 2
                        ? selected.join(', ')
                        : `${selected.length} selected`;
                    }}
                    displayEmpty
                    sx={{
                      backgroundColor: '#fff',
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: COLORS.PRIMARY,
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: COLORS.PRIMARY,
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: COLORS.PRIMARY,
                      },
                    }}
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          maxHeight: 200,
                          width: 280,
                          overflow: 'auto',
                          marginTop: '4px',
                          boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.15)',
                          border: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                          '& .MuiMenuItem-root': {
                            minHeight: 36,
                            padding: '4px 12px',
                            fontSize: '0.85rem',
                          },
                          '& .MuiCheckbox-root': {
                            padding: '4px 6px 4px 4px',
                          }
                        }
                      },
                    }}
                  >
                    {loadingFilters.clients ? (
                      <MenuItem disabled sx={{ justifyContent: 'center', py: 1 }}>
                        <CircularProgress size={18} />
                      </MenuItem>
                    ) : clients.length > 0 ? (
                      clients.map((client) => (
                        <MenuItem
                          key={client._id}
                          value={client.name || client.companyName}
                          sx={{
                            whiteSpace: 'normal',
                            wordBreak: 'break-word',
                            '&:hover': {
                              backgroundColor: 'rgba(0, 0, 0, 0.04)',
                            }
                          }}
                        >
                          <Checkbox
                            checked={selectedClients.indexOf(client.name || client.companyName) > -1}
                            size="small"
                            sx={{ '& svg': { fontSize: '0.9rem' } }}
                          />
                          <ListItemText
                            primary={client.name || client.companyName}
                            sx={{
                              margin: 0,
                              '& .MuiTypography-root': {
                                fontSize: '0.85rem',
                                fontWeight: 400,
                                lineHeight: 1.2,
                              }
                            }}
                          />
                        </MenuItem>
                      ))
                    ) : (
                      <MenuItem disabled sx={{ py: 1 }}>
                        <Typography sx={{
                          fontSize: '0.85rem',
                          color: COLORS.TEXT,
                          fontStyle: 'italic'
                        }}>
                          No clients available
                        </Typography>
                      </MenuItem>
                    )}
                  </Select>
                </FormControl>
              </Box>
            )}

            {/* Site Check Type Filter */}
            <Box sx={{ flex: 1, minWidth: 180 }}>
              <Typography
                sx={{
                  ...TYPOGRAPHY.BODY_SMALL,
                  color: COLORS.PRIMARY,
                  mb: 0.5,
                  fontWeight: 600
                }}
              >
                Site Check Type
              </Typography>
              <FormControl fullWidth size="small">
                <Select
                  multiple
                  value={selectedTemplates}
                  onChange={(e) => {
                    setSelectedTemplates(e.target.value);
                    setPage(1);
                  }}
                  input={<OutlinedInput />}
                  renderValue={(selected) => {
                    if (selected.length === 0) {
                      return (
                        <Typography sx={{
                          color: COLORS.LIGHT_SECONDARY,
                          fontSize: '0.875rem'
                        }}>
                          Select Site Check Types
                        </Typography>
                      );
                    }
                    return selected.length <= 2
                      ? selected.join(', ')
                      : `${selected.length} selected`;
                  }}
                  displayEmpty
                  sx={{
                    backgroundColor: '#fff',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: COLORS.PRIMARY,
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: COLORS.PRIMARY,
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: COLORS.PRIMARY,
                    },
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        maxHeight: 200,
                        width: 280,
                        overflow: 'auto',
                        marginTop: '4px',
                        boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.15)',
                        border: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                        '& .MuiMenuItem-root': {
                          minHeight: 36,
                          padding: '4px 12px',
                          fontSize: '0.85rem',
                        },
                        '& .MuiCheckbox-root': {
                          padding: '4px 6px 4px 4px',
                        }
                      }
                    },
                  }}
                >
                  {loadingFilters.templates ? (
                    <MenuItem disabled sx={{ justifyContent: 'center', py: 1 }}>
                      <CircularProgress size={18} />
                    </MenuItem>
                  ) : templates.length > 0 ? (
                    templates.map((template) => (
                      <MenuItem
                        key={template._id}
                        value={template.title}
                        sx={{
                          whiteSpace: 'normal',
                          wordBreak: 'break-word',
                          '&:hover': {
                            backgroundColor: 'rgba(0, 0, 0, 0.04)',
                          }
                        }}
                      >
                        <Checkbox
                          checked={selectedTemplates.indexOf(template.title) > -1}
                          size="small"
                          sx={{ '& svg': { fontSize: '0.9rem' } }}
                        />
                        <ListItemText
                          primary={template.title}
                          sx={{
                            margin: 0,
                            '& .MuiTypography-root': {
                              fontSize: '0.85rem',
                              fontWeight: 400,
                              lineHeight: 1.2,
                            }
                          }}
                        />
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem disabled sx={{ py: 1 }}>
                      <Typography sx={{
                        fontSize: '0.85rem',
                        color: COLORS.TEXT,
                        fontStyle: 'italic'
                      }}>
                        No templates available
                      </Typography>
                    </MenuItem>
                  )}
                </Select>
              </FormControl>
            </Box>
          </Box>
        )}

        {/* Error Message */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Table Container */}
        <Box sx={{
          width: '100%',
          overflowX: 'auto',
          WebkitOverflowScrolling: 'touch',
          '&::-webkit-scrollbar': {
            height: '6px',
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: '#f1f1f1',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: COLORS.LIGHT_SECONDARY,
            borderRadius: '4px',
          },
        }}>
          {/* Table */}
          <Paper elevation={0} sx={{
            backgroundColor: "transparent",
            minWidth: '1000px'
          }}>
            <TableContainer>
              <Table
                sx={{
                  backgroundColor: "transparent",
                  "& thead th": {
                    backgroundColor: "transparent",
                    padding: "8px 6px",
                  },
                  "& tbody td": {
                    backgroundColor: "transparent",
                    borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                    padding: "8px 6px",
                    verticalAlign: 'top',
                  },
                  tableLayout: 'fixed',
                  width: '100%',
                }}
              >
                <TableHead>
                  <TableRow>
                    {getTableHeaders().map((head) => (
                      <TableCell
                        key={head.key}
                        sx={{
                          ...TYPOGRAPHY.H4,
                          color: COLORS.PRIMARY,
                          borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                          whiteSpace: "nowrap",
                          fontSize: "0.85rem",
                          fontWeight: 600,
                          width: head.width,
                          minWidth: head.width,
                          maxWidth: head.width,
                          padding: "8px 6px",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: 0.5,
                          }}
                        >
                          <span style={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}>
                            {head.key}
                          </span>
                          {head.hasSort && (
                            <CustomTooltip title={`Sort by ${head.key}`}>
                              <Icon
                                name={getSortIconForColumn(head.sortKey)}
                                color={getSortIconColorForColumn(head.sortKey)}
                                sx={{
                                  fontSize: 14,
                                  cursor: "pointer",
                                  flexShrink: 0,
                                  "&:hover": {
                                    color: COLORS.PRIMARY,
                                  },
                                }}
                                onClick={() => handleColumnSort(head.sortKey)}
                              />
                            </CustomTooltip>
                          )}
                        </Box>
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>

                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={getTableHeaders().length} align="center" sx={{ py: 3 }}>
                        <CircularProgress />
                      </TableCell>
                    </TableRow>
                  ) : projects.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={getTableHeaders().length} align="center" sx={{ py: 3 }}>
                        <Typography sx={{ color: COLORS.TEXT }}>
                          {!isFilterApplied()
                            ? `No ${statusDisplayNames[status]} projects found`
                            : `No ${statusDisplayNames[status]} projects found with selected filters`}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    projects.map((project) => (
                      <TableRow key={project.id} sx={{ borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}` }}>
                        {/* Project Name Column */}
                        <TableCell sx={{
                          width: '160px',
                          minWidth: '160px',
                          maxWidth: '160px',
                          padding: "8px 6px"
                        }}>
                          {/* Report Number Badge - Only show for FINALREPORT status */}
                          {project.status === "FINALREPORT" && project.projectReportNo && project.projectReportNo !== "—" ? (
                            <Box
                              sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                padding: '2px 8px',
                                borderRadius: '12px',
                                border: `1.5px solid ${COLORS.PRIMARY}`,
                                backgroundColor: COLORS.SECONDARY,
                                color: COLORS.BACKGROUND,
                                fontSize: '0.7rem',
                                fontWeight: 600,
                                marginBottom: '6px',
                                width: 'fit-content',
                                minWidth: '60px',
                                textAlign: 'center',
                                lineHeight: 1.2,
                                textTransform: 'uppercase',
                                letterSpacing: '0.3px'
                              }}
                            >
                              {project.projectReportNo}
                            </Box>
                          ) : (
                            <Typography
                              sx={{
                                ...TYPOGRAPHY.BODY_SMALL,
                                color: COLORS.LIGHT_SECONDARY,
                                fontSize: "0.7rem",
                                marginBottom: '6px'
                              }}
                            >
                              —
                            </Typography>
                          )}
                          <Typography
                            sx={{
                              ...TYPOGRAPHY.BODY_LARGE,
                              color: COLORS.TEXT,
                              fontSize: "0.8rem",
                              wordBreak: 'break-word',
                              lineHeight: 1.3,
                              mb: 0.5
                            }}
                          >
                            {project.rawItem?.source === "FIR" || project.isFirRequest || project.source === "FIR" || project.firDetails
                              ? (project.propertyName || project.firDetails?.propertyName || project.projectName || project.firDetails?.projectName || project.buildingName || project.firDetails?.buildingName || `FIR Request (${project.firDetails?.propertyType || project.propertyType || 'Unknown'})`)
                              : (project.projectName || project.propertyName || project.buildingName || `FIR Request (${project.firDetails?.propertyType || project.propertyType || 'Unknown'})`)
                            }
                          </Typography>
                          <Typography
                            sx={{
                              ...TYPOGRAPHY.BODY_SMALL,
                              color: COLORS.TEXT,
                              fontSize: "0.7rem",
                              wordBreak: 'break-word',
                              lineHeight: 1.2
                            }}
                          >
                            {project.clientAddress}
                          </Typography>
                        </TableCell>

                        {/* Client Name Column */}
                        <TableCell sx={{
                          width: '130px',
                          minWidth: '130px',
                          maxWidth: '130px',
                          padding: "8px 6px"
                        }}>
                          <Typography
                            sx={{
                              ...TYPOGRAPHY.BODY_LARGE,
                              color: COLORS.TEXT,
                              fontSize: "0.8rem",
                              wordBreak: 'break-word',
                              lineHeight: 1.3,
                              mb: 0.5
                            }}
                          >
                            {project.clientName}
                          </Typography>
                          <Typography
                            sx={{
                              ...TYPOGRAPHY.BODY_SMALL,
                              color: COLORS.TEXT,
                              fontSize: "0.7rem",
                              wordBreak: 'break-word',
                              lineHeight: 1.2
                            }}
                          >
                            {project.clientContact}
                          </Typography>
                        </TableCell>

                        {/* Engineer Column (only for ADMIN/SUPERADMIN) */}
                        {(hasRole("ADMIN") || hasRole("SUPERADMIN")) && (
                          <TableCell sx={{
                            width: '120px',
                            minWidth: '120px',
                            maxWidth: '120px',
                            padding: "8px 6px"
                          }}>
                            <Typography
                              sx={{
                                ...TYPOGRAPHY.BODY_LARGE,
                                color: COLORS.TEXT,
                                fontSize: "0.8rem",
                                wordBreak: 'break-word',
                                lineHeight: 1.3,
                                mb: 0.5
                              }}
                            >
                              {project.engineer}
                            </Typography>
                            {project.engineerContact && (
                              <Typography
                                sx={{
                                  ...TYPOGRAPHY.BODY_SMALL,
                                  color: COLORS.TEXT,
                                  fontSize: "0.7rem",
                                  wordBreak: 'break-word',
                                  lineHeight: 1.2
                                }}
                              >
                                {project.engineerContact}
                              </Typography>
                            )}
                          </TableCell>
                        )}

                        {/* Site Check Column */}
                        <TableCell
                          sx={{
                            width: '100px',
                            minWidth: '100px',
                            maxWidth: '100px',
                            padding: "8px 6px",
                            color: COLORS.TEXT,
                            fontSize: "0.8rem",
                            wordBreak: 'break-word',
                            lineHeight: 1.3
                          }}
                        >
                          {project.siteCheck}
                        </TableCell>

                        {/* Request Date Column */}
                        <TableCell
                          sx={{
                            width: '80px',
                            minWidth: '80px',
                            maxWidth: '80px',
                            padding: "8px 6px",
                            color: COLORS.TEXT,
                            fontSize: "0.8rem",
                            wordBreak: 'break-word',
                            lineHeight: 1.3
                          }}
                        >
                          {project.reqDate}
                        </TableCell>

                        {/* Visit Date Column */}
                        <TableCell
                          sx={{
                            width: '100px',
                            minWidth: '100px',
                            maxWidth: '100px',
                            padding: "8.8px 6.8px",
                            color: COLORS.TEXT,
                            fontSize: "0.8rem",
                            wordBreak: 'break-word',
                            lineHeight: 1.3
                          }}
                        >
                          {project.assignedVisitDate}
                        </TableCell>

                        {/* Status Column */}
                        <TableCell sx={{
                          width: '90px',
                          minWidth: '90px',
                          maxWidth: '90px',
                          padding: "8px 6px"
                        }}>
                          <Chip
                            label={project.status}
                            size="small"
                            sx={{
                              ...(statusStyles[project.status] || {}),
                              height: 20,
                              ...TYPOGRAPHY.BODY_SMALL,
                              fontSize: "0.65rem",
                              textTransform: 'capitalize',
                              '& .MuiChip-label': {
                                padding: '0 6px',
                              }
                            }}
                          />
                        </TableCell>

                        {/* Actions Column */}
                        <TableCell sx={{
                          width: '100px',
                          minWidth: '100px',
                          maxWidth: '100px',
                          padding: "8px 6px"
                        }}>
                          <Box sx={{ display: "flex", gap: 0.25, flexWrap: 'wrap' }}>
                            {/* View Details - Always visible for all roles */}
                            <CustomTooltip title="View Details">
                              <IconButton
                                size="small"
                                onClick={() => handleViewDetails(project)}
                                sx={{
                                  padding: "2px",
                                  minWidth: '24px',
                                  height: '24px'
                                }}
                              >
                                <Icon name="Action1" color={COLORS.TEXT} />
                              </IconButton>
                            </CustomTooltip>

                            {/* Edit - For ADMIN/SUPERADMIN and ENGINEER (but not for CLIENT) */}
                            {(hasRole("ADMIN") || hasRole("SUPERADMIN") || hasRole("ENGINEER")) && (
                              <CustomTooltip title="Edit">
                                <IconButton
                                  size="small"
                                  onClick={() => handleEdit(project._id)}
                                  sx={{
                                    padding: "2px",
                                    minWidth: '24px',
                                    height: '24px'
                                  }}
                                >
                                  <Icon name="Action3" color={COLORS.TEXT} />
                                </IconButton>
                              </CustomTooltip>
                            )}

                            {/* Delete - Only for ADMIN/SUPERADMIN */}
                            {(hasRole("ADMIN") || hasRole("SUPERADMIN")) && (
                              <CustomTooltip title="Delete">
                                <IconButton
                                  size="small"
                                  onClick={() => handleDelete(project)}
                                  sx={{
                                    padding: "2px",
                                    minWidth: '24px',
                                    height: '24px'
                                  }}
                                >
                                  <Icon name="Action4" color={COLORS.ERROR} />
                                </IconButton>
                              </CustomTooltip>
                            )}

                            {/* Download Report - Only for CLIENT when status is FINALREPORT */}
                            {(hasRole("CLIENT") || hasRole("ENGINEER") || hasRole("ADMIN") || hasRole("SUPERADMIN")) &&
                              project.status === "FINALREPORT" && (
                                <CustomTooltip title="Download Report">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleDownloadReport(project._id)}
                                    disabled={downloadingProjectId === project._id}
                                    sx={{
                                      padding: "1.5px",
                                      minWidth: '20px',
                                      height: '20px'
                                    }}
                                  >
                                    {downloadingProjectId === project._id ? (
                                      <CircularProgress size={16} color="inherit" />
                                    ) : (
                                      <Icon name="download1" color={COLORS.SECONDARY} />
                                    )}
                                  </IconButton>
                                </CustomTooltip>
                              )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Pagination */}
            {!loading && projects.length > 0 && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  alignItems: "center",
                  gap: 2,
                  p: 2,
                  mt: 2,
                  width: '100%',
                  overflowX: 'auto',
                }}
              >
                <Typography sx={{ fontSize: 13, color: "#777", whiteSpace: 'nowrap' }}>
                  {startIndex} - {endIndex} of {totalProjects} projects
                </Typography>

                <Box sx={{ display: "flex", gap: 1, alignItems: 'center' }}>
                  <IconButton
                    onClick={() => handlePageChange(page - 1)}
                    disabled={page === 1}
                    size="small"
                    sx={{ padding: '4px' }}
                  >
                    <Icon name="chevron-left" size={18} />
                  </IconButton>

                  {Array.from({ length: Math.min(totalPages, 3) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 3) {
                      pageNum = i + 1;
                    } else if (page === 1) {
                      pageNum = i + 1;
                    } else if (page === totalPages) {
                      pageNum = totalPages - 2 + i;
                    } else {
                      pageNum = page - 1 + i;
                    }

                    if (pageNum > 0 && pageNum <= totalPages) {
                      return (
                        <Button
                          key={pageNum}
                          onClick={() => handlePageChange(pageNum)}
                          sx={{
                            minWidth: 24,
                            height: 24,
                            fontSize: "0.7rem",
                            bgcolor: page === pageNum ? COLORS.PRIMARY : "transparent",
                            color: page === pageNum ? "#fff" : COLORS.TEXT,
                            border: page === pageNum ? "none" : "1px solid #E1BEE7",
                            "&:hover": {
                              bgcolor: page === pageNum ? COLORS.PRIMARY : "rgba(0,0,0,0.04)"
                            },
                          }}
                        >
                          {pageNum}
                        </Button>
                      );
                    }
                    return null;
                  })}

                  <IconButton
                    onClick={() => handlePageChange(page + 1)}
                    disabled={page === totalPages}
                    size="small"
                    sx={{ padding: '4px' }}
                  >
                    <Icon name="chevron-right" size={18} />
                  </IconButton>
                </Box>
              </Box>
            )}
          </Paper>
        </Box>

        {/* Global Sort Menu */}
        <Menu
          anchorEl={sortAnchorEl}
          open={Boolean(sortAnchorEl)}
          onClose={() => setSortAnchorEl(null)}
          PaperProps={{
            sx: {
              borderRadius: 2,
              boxShadow: "0px 6px 18px rgba(0,0,0,0.15)",
              px: 0.5,
            },
          }}
        >
          {[
            { label: "Latest First", value: "latest", icon: "foldUp" },
            { label: "Oldest First", value: "oldest", icon: "foldDown" },
            { label: "A to Z", value: "AZ", icon: "foldUp" },
            { label: "Z to A", value: "ZA", icon: "foldDown" },
          ].map((item) => {
            const isActive = selectedSort === item.value;

            return (
              <MenuItem
                key={item.value}
                onClick={() => {
                  setSelectedSort(item.value);
                  // Clear column sort when global sort is selected
                  setSortConfig({ column: null, direction: null });
                  setSortAnchorEl(null);
                }}
                sx={{
                  gap: 1.5,
                  borderRadius: 1,
                  bgcolor: "transparent !important",
                  "&:hover": { bgcolor: "transparent" },
                  "&.Mui-selected": { bgcolor: "transparent" },
                  color: isActive ? COLORS.PRIMARY : COLORS.TEXT,
                }}
              >
                <Icon
                  name={item.icon}
                  size={18}
                  color={isActive ? COLORS.PRIMARY : COLORS.TEXT}
                />
                <Typography sx={TYPOGRAPHY.BODY_LARGE}>
                  {item.label}
                </Typography>
              </MenuItem>
            );
          })}
        </Menu>

        {/* Calendar Menu with Icons */}
        <Menu
          anchorEl={calendarAnchorEl}
          open={Boolean(calendarAnchorEl)}
          onClose={() => setCalendarAnchorEl(null)}
          PaperProps={{
            sx: {
              borderRadius: 2,
              boxShadow: "0px 6px 18px rgba(0,0,0,0.15)",
              px: 0.5,
            },
          }}
        >
          {[
            { label: "All Dates", value: "ALL", icon: "calendarAll" },
            { label: "Today", value: "TODAY", icon: "calendarToday" },
            { label: "Yesterday", value: "YESTERDAY", icon: "calendarYesterday" },
            { label: "Tomorrow", value: "TOMORROW", icon: "calendarTomorrow" },
            { label: "Custom Date Range", value: "CUSTOM", icon: "calendarCustom" },
          ].map((item) => {
            const isActive = dateFilter === item.value;

            return (
              <MenuItem
                key={item.value}
                onClick={() => handleCalendarFilterSelect(item.value)}
                sx={{
                  gap: 1.5,
                  borderRadius: 1,
                  bgcolor: "transparent !important",
                  "&:hover": { bgcolor: "transparent" },
                  color: isActive ? COLORS.PRIMARY : COLORS.TEXT,
                }}
              >
                <Icon
                  name={item.icon}
                  size={18}
                  color={isActive ? COLORS.PRIMARY : COLORS.TEXT}
                />
                <Typography sx={TYPOGRAPHY.BODY_LARGE}>
                  {item.label}
                </Typography>
              </MenuItem>
            );
          })}
        </Menu>

        {/* Custom Date Range Picker Dialog */}
        <Dialog
          open={showDatePicker}
          onClose={() => setShowDatePicker(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Icon name="calendarCustom" color={COLORS.PRIMARY} />
              <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.PRIMARY }}>
                Select Date Range
              </Typography>
            </Box>
          </DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 2 }}>
              <DatePicker
                label="From Date"
                value={customDateRange.from}
                onChange={(newValue) => {
                  setCustomDateRange(prev => ({ ...prev, from: newValue }));
                }}
                renderInput={(params) => <TextField {...params} fullWidth />}
                maxDate={customDateRange.to || new Date()}
              />
              <DatePicker
                label="To Date"
                value={customDateRange.to}
                onChange={(newValue) => {
                  setCustomDateRange(prev => ({ ...prev, to: newValue }));
                }}
                renderInput={(params) => <TextField {...params} fullWidth />}
                minDate={customDateRange.from}
                maxDate={new Date()}
              />

            </Box>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 3 }}>
            <Button
              onClick={() => setShowDatePicker(false)}
              sx={{
                color: COLORS.TEXT,
                "&:hover": { bgcolor: "rgba(0,0,0,0.04)" }
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleCustomDateRangeSelect}
              disabled={!customDateRange.from || !customDateRange.to}
              sx={{
                bgcolor: COLORS.SECONDARY,
                color: "#fff",
                "&:hover": { bgcolor: COLORS.SECONDARY },
                "&:disabled": { bgcolor: COLORS.LIGHT_SECONDARY }
              }}
            >
              Apply Date Range
            </Button>
          </DialogActions>
        </Dialog>


        <ClientDetailsSidebar
          open={isSidebarOpen}
          onClose={() => {
            setIsSidebarOpen(false);
            setSelectedProject(null);
          }}
          projectId={selectedProject}
        />

        {isEditScreenOpen && (
          <EditChecklistSidebarWithToast
            open={isEditScreenOpen}
            onClose={() => {
              setIsEditScreenOpen(false);
              setSelectedProject(null);
              setIsEditMode(false);
              fetchProjects();
            }}
            projectId={selectedProject}
            mode={isEditMode ? "edit" : "view"}
          />
        )}

        <ConfirmDialog
          open={deleteConfirm.open}
          title="Confirm Delete"
          message={
            deleteConfirm.status && ["ASSIGNED", "VISITED", "SIGNPENDING", "FINALREPORT"].includes(deleteConfirm.status)
              ? `This project is already marked as ${statusDisplayNames[deleteConfirm.status] || deleteConfirm.status}. Are you sure you want to delete this request? This action cannot be undone.`
              : "Are you sure you want to delete this request? This action cannot be undone."
          }
          onConfirm={executeDelete}
          onCancel={() => setDeleteConfirm({ open: false, id: null, status: null })}
        />
      </Box>
    </LocalizationProvider>
  );
};

export default StatusBasedProjects;